import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import classNames from "classnames";
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import useScreenSize from "../../../../../hooks/useScreenSize";
import useTouchScroll from "../../../../../hooks/useTouchScroll";
import photo1 from "../../../../imagenes/home/photo-01-uncompressed.webp";
import photo2 from "../../../../imagenes/home/photo-02-uncompressed.webp";
import photo3 from "../../../../imagenes/home/photo-03-uncompressed.webp";
import photo4 from "../../../../imagenes/home/photo-04-uncompressed.webp";
import photo5 from "../../../../imagenes/home/photo-05-uncompressed.webp";
import photo6 from "../../../../imagenes/home/photo-06-uncompressed.webp";
import Button from "../../../../ui/Button/Button";
import "./Carousel.scss";

type CarouselItem = {
	img: string;
	alt?: string;
};

type CarouselControls = {
	prev: string;
	next: string;
};

export type ControlsGroup = {
	handlePrev: () => void;
	handleNext: () => void;
};

export type CarouselContent = {
	enabled: boolean;
	items: CarouselItem[];
	controls: CarouselControls;
};

const PhotosMap = {
	"photo-01": photo1,
	"photo-02": photo2,
	"photo-03": photo3,
	"photo-04": photo4,
	"photo-05": photo5,
	"photo-06": photo6,
};

const ControlsGroup = ({ handleNext, handlePrev }: ControlsGroup) => {
	return (
		<div
			className="carousel__controls"
			role="group"
			aria-label="Carousel controls">
			<Button
				aria-label="Previous"
				className={classNames("button--control button--prev")}
				onClick={handlePrev}>
				<ChevronLeft sx={{ fontSize: "2rem" }} />
			</Button>
			<Button
				aria-label="Next"
				className={classNames("button--control button--next")}
				onClick={handleNext}>
				<ChevronRight sx={{ fontSize: "2rem" }} />
			</Button>
		</div>
	);
};

const Carousel = ({
	sectionContent,
	isMobile,
}: {
	sectionContent: CarouselContent;
	isMobile: boolean;
}) => {
	const direction = useTouchScroll();
	const { width } = useScreenSize();
	const imgWidth = isMobile ? width : 450;
	const containerTotalWidth = sectionContent.items.length * imgWidth;
	const totalPages = Math.ceil(containerTotalWidth / width);
	const [currentPage, setCurrentPage] = useState(1);
	const lastDirection = useRef<string | null>(null);
	const translateValue =
		currentPage === 1
			? 0
			: currentPage === totalPages
				? containerTotalWidth - width
				: imgWidth * (currentPage - 1);

	const handlePrev = useCallback(() => {
		if (currentPage <= 1) {
			return setCurrentPage(totalPages);
		}
		setCurrentPage(currentPage - 1);
	}, [currentPage, totalPages]);

	const handleNext = useCallback(() => {
		if (currentPage >= totalPages) {
			return setCurrentPage(1);
		}
		setCurrentPage(currentPage + 1);
	}, [currentPage, totalPages]);

	useEffect(() => {
		if (direction && direction !== lastDirection.current) {
			if (direction === "left") handleNext();
			if (direction === "right") handlePrev();

			lastDirection.current = direction;
		} else if (!direction) {
			lastDirection.current = null;
		}
	}, [direction, handleNext, handlePrev]);

	return (
		<section className="carousel">
			<ul
				className="carousel__container"
				style={
					{
						"--carousel-container-translate-x": `-${translateValue}px`,
					} as CSSProperties
				}>
				{sectionContent.items.map((item, index) => (
					<li
						key={index}
						className="carousel__item"
						style={
							{ "--carousel-item-width": `${imgWidth}px` } as CSSProperties
						}>
						<img
							className="carousel__img"
							src={PhotosMap[item.img as keyof typeof PhotosMap]}
							alt={item.alt || ""}
						/>
					</li>
				))}
			</ul>
			{!isMobile && (
				<ControlsGroup handleNext={handleNext} handlePrev={handlePrev} />
			)}
		</section>
	);
};

export default Carousel;
